import {
	type ComponentProps,
	createContext,
	type JSX,
	onCleanup,
	onMount,
	splitProps
} from "solid-js";
import { useCustomContext } from "~/solidJs/shared";

const ViewportContext = createContext<{
	registerElement: (element: HTMLElement, callback: () => void) => void;
	unregisterElement: (element: HTMLElement) => void;
}>({ registerElement: () => {}, unregisterElement: () => {} });

export function ViewPortRoot(props: { children: JSX.Element }) {
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const callback = callbacksRef.get(entry.target);
				if (callback && entry.isIntersecting) {
					callback(entry.isIntersecting);
				}
			});
		},
		{
			threshold: 0.1
		}
	);
	const callbacksRef = new Map();

	onCleanup(() => {
		observer.disconnect();
	});

	const registerElement = (element: HTMLElement, callback: () => void) => {
		callbacksRef.set(element, callback);
		observer.observe(element);
	};

	const unregisterElement = (element: HTMLElement) => {
		callbacksRef.delete(element);
		observer.unobserve(element);
	};

	return (
		<ViewportContext.Provider value={{ registerElement, unregisterElement }}>
			{props.children}
		</ViewportContext.Provider>
	);
}

export function ViewportElement(props: { onEnterViewport: () => void } & ComponentProps<"div">) {
	let elementRef: HTMLDivElement;
	const viewportContext = useCustomContext(ViewportContext);
	const [_customProps, nativeProps] = splitProps(props, ["onEnterViewport"]);
	onMount(() => {
		if (elementRef) {
			viewportContext.registerElement(elementRef, props.onEnterViewport);
		}
		onCleanup(() => {
			if (elementRef) {
				viewportContext.unregisterElement(elementRef);
			}
		});
	});

	return (
		<div ref={elementRef!} {...nativeProps}>
			{props.children}
		</div>
	);
}
